import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import {
  ActionIcon,
  Avatar,
  Button,
  Group,
  Loader,
  Popover,
  Stack,
  TextInput,
  Text,
} from "@mantine/core";
import { mtsUrl, requestResourceUrl } from "../lib/mts-url";
import { UserFields } from "../types/user.types";
import csrfToken from "../lib/csrf-token";
import UserCheckboxCard from "./UserCheckboxCard";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconX } from "@tabler/icons-react";
import { RequestBaseFields } from "../types/request.types";
import UserAvatar from "./UserAvatar";
import Can from "./Can";
import UserAvatarWithCard from "./UserAvatarWithCard";

interface AssignmentFormFields {
  assignee_ids: number[];
}

interface AssignmentProps {
  request: RequestBaseFields;
}

export default function Assignment({ request }: AssignmentProps) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [userIds, setUserIds] = useState(new Set<number>());
  const queryClient = useQueryClient();
  let userCards = [];
  let avatarGroup;

  const usersQuery = useQuery({
    queryKey: ['assignees'],
    queryFn: () => axios.get(mtsUrl('assignees.json'))
  });

  const assignedQuery = useQuery({
    queryKey: [request.request_type, request.id, 'assignments'],
    queryFn: () => axios.get(requestResourceUrl(request, 'assignments.json'))
  });

  const updateAssignment = useMutation({
    mutationFn: (fields: AssignmentFormFields) => {
      return axios.put(
        requestResourceUrl(request, 'assignments/update'),
        { user_ids: fields.assignee_ids },
        {
          withCredentials: true,
          headers: { 'X-CSRF-Token': csrfToken() }
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requests', 'all'] });
      queryClient.invalidateQueries({
        queryKey: [request.request_type, request.id, 'assignments']
      });
      setOpen(false);
    },
    onError: () => {
      notifications.show({
        message: 'There was an error saving the assignment.',
        icon: <IconX />,
        color: 'red',
        position: 'top-center'
      });
    },
    onSettled: () => setSaving(false)
  });

  const handleSaveClick = () => {
    setSaving(true);
    updateAssignment.mutate({assignee_ids: [...userIds]});
  };

  if (usersQuery.isSuccess) {
    let userData = usersQuery.data.data;
    const q = userQuery.toLowerCase();

    if (userQuery && userQuery.length > 0) {
      userData = userData.filter((u: UserFields) =>
        (u.name && u.name.toLowerCase().search(q) >= 0) ||
        u.username.toLowerCase().search(q) >= 0
      );
    }
    
    userCards = userData.map((u: UserFields) =>
      <UserCheckboxCard
        key={u.id}
        user={u}
        checked={userIds.has(u.id)}
        onCheck={(usr: UserFields, c: boolean) => {
          setUserIds(ids => {
            const newIds = new Set(ids);

            if (c)
              newIds.add(usr.id);
            else
              newIds.delete(usr.id);

            return newIds;
          });
          setSaveEnabled(true);
        }}
      />
    );
  }

  if (
    assignedQuery.isSuccess &&
    assignedQuery.data.data &&
    assignedQuery.data.data.length > 0
  ) {
    avatarGroup = (
      <Avatar.Group>
        {assignedQuery.data.data.map((u: UserFields) =>
          <UserAvatarWithCard key={u.id} user={u} />
        )}
      </Avatar.Group>
    );
  } else {
    avatarGroup = (
      <Text size="sm" c="gray.6">
        No assignment
      </Text>
    );
  }

  const onTargetClick = () => {
    if (!open && assignedQuery.isSuccess) {
      setUserIds(new Set(assignedQuery.data.data.map((u: UserFields) => u.id)));
    }
      
    setOpen(!open);
  };

  return (
    <Group>
      {avatarGroup}
      <Can do="update_assignment" on="Request">
        <Popover
          opened={open}
          onChange={setOpen}
          withArrow
          arrowSize={12}
          shadow="lg"
        >
          <Popover.Target>
            <ActionIcon
              variant="transparent"
              c="gray.9"
              size="sm"
              pt={0}
              mt={0}
              onClick={onTargetClick}
            >
              <IconEdit />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown>
            <TextInput
              value={userQuery}
              placeholder="Filter users"
              onChange={e => setUserQuery(e.target.value)}
              autoFocus
            />
            <Stack mt="xs" gap="xs">
              {userCards}
              <Button
                type="submit"
                disabled={!saveEnabled || saving}
                onClick={handleSaveClick}
              >
                {saving && <Loader mr="sm" color="gray.5" size={14} />}
                Save
              </Button>
            </Stack>
          </Popover.Dropdown>
        </Popover>
      </Can>
    </Group>
  );
}
